<script lang="ts" setup>
import type { UtrLeaderboardToken } from '@forgd/contract/types'
import { formatValue } from '@forgd/formatter'
import { ProjectAvatar } from '@forgd/ui-kit/blocks/avatars'
import { Button } from '@forgd/ui-kit/button'
import { Skeleton } from '@forgd/ui-kit/skeleton'
import { Stat, StatContent, StatHeader, StatLabel, StatValue } from '@forgd/ui-kit/stat'
import { PlayIcon } from '@heroicons/vue/24/outline'
import { useQuery } from '@pinia/colada'

const GRADES = ['AAA', 'AA', 'A', 'BBB', 'BB', 'B', 'CCC']

const query = useQuery({
  ...buildApiQuerySSRSafe('/universal-token-rating/leaderboard'),
  refetchOnWindowFocus: false,
})

const isVideoOpen = ref(false)

const ratedTokens = computed(() =>
  (query.data.value?.tokens ?? []).filter(token => !token.notRated && !!token.grade && GRADES.includes(token.grade)),
)

const tokensScored = computed(() => formatValue(query.data.value?.scoredCount ?? 0, 'number:0'))

const topToken = computed(() =>
  ratedTokens.value.reduce<UtrLeaderboardToken | null>((top, token) => (!top || token.score > top.score ? token : top), null),
)

const distribution = computed(() => {
  const total = ratedTokens.value.length
  return GRADES.map((grade) => {
    const count = ratedTokens.value.filter(token => token.grade === grade).length
    return {
      grade,
      count,
      label: `${grade}: ${formatValue(total ? (count / total) * 100 : 0, 'percent:2')}`,
    }
  })
})
</script>

<template>
  <section
    data-el="PrUtrHomeBanner"
    class="flex flex-col overflow-hidden rounded-lg bg-light md:flex-row"
  >
    <div class="flex flex-col justify-center gap-5 p-4 md:w-1/2 md:p-8">
      <div class="flex max-w-137.5 flex-col gap-4">
        <h3 class="font-regular text-lg leading-normal text-primal md:text-3xl md:leading-tight">
          Universal Token Ratings
        </h3>
        <p class="text-sm text-secondary">
          Assess project quality with a powerful combination of disclosures and performance grades and scores. Claim token profiles and Improve token rating, or request to be listed.
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <Button as-child size="sm">
          <NuxtLink to="/universal-token-rating">
            Explore UTR
          </NuxtLink>
        </Button>
        <Button variant="outline" size="sm" @click="isVideoOpen = true">
          Watch the Demo
          <PlayIcon />
        </Button>
      </div>
    </div>

    <div
      v-if="!query.error.value"
      class="flex flex-col border-t border-default md:w-1/2 md:border-t-0 md:border-l"
    >
      <div class="flex border-b border-default">
        <Stat class="flex-1 justify-start border-r border-default p-4">
          <StatHeader>
            <StatLabel class="font-normal">
              Tokens Scored
            </StatLabel>
          </StatHeader>
          <Skeleton v-if="query.isLoading.value" class="h-6 w-12" />
          <StatContent v-else>
            <StatValue>{{ tokensScored }}</StatValue>
          </StatContent>
        </Stat>

        <Stat class="flex-1 justify-start p-4">
          <StatHeader>
            <StatLabel class="font-normal">
              Highest Scored Token
            </StatLabel>
          </StatHeader>
          <Skeleton v-if="query.isLoading.value" class="h-6 w-32" />
          <div v-else-if="topToken" class="flex w-full flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <StatContent class="gap-2">
              <ProjectAvatar :src="topToken.image" size="xs" :alt="topToken.name" bordered />
              <StatValue>{{ topToken.ticker ?? topToken.name }}</StatValue>
              <PrUtrTokenGradeChip v-if="topToken.grade" :grade="topToken.grade" class="h-5" />
            </StatContent>
            <div class="flex items-center gap-1 text-xs whitespace-nowrap">
              <span class="text-secondary">Overall Score</span>
              <span class="text-kpi-xs text-primal">{{ formatValue(topToken.score, 'number:2') }}</span>
            </div>
          </div>
        </Stat>
      </div>

      <Stat class="flex-1 justify-start p-4">
        <StatHeader>
          <StatLabel class="font-normal">
            Token Grade Distribution
          </StatLabel>
        </StatHeader>
        <div class="flex w-full flex-1 flex-col gap-2">
          <Skeleton v-if="query.isLoading.value" class="h-12.5 w-full md:h-auto md:min-h-12.5 md:flex-1" />
          <div v-else class="flex h-12.5 w-full gap-1 md:h-auto md:min-h-12.5 md:flex-1">
            <template v-for="item in distribution" :key="item.grade">
              <div
                v-if="item.count"
                class="basis-0"
                :class="utrLetterGradeChipClass(item.grade)"
                :style="{ flexGrow: item.count }"
              />
            </template>
          </div>
          <div v-if="!query.isLoading.value" class="flex flex-wrap gap-2">
            <div v-for="item in distribution" :key="item.grade" class="flex items-center gap-1">
              <span class="w-1 self-stretch rounded-full" :class="utrLetterGradeChipClass(item.grade)" />
              <span class="text-xs whitespace-nowrap text-primal">{{ item.label }}</span>
            </div>
          </div>
        </div>
      </Stat>
    </div>

    <PrUtrVideoModal v-model:open="isVideoOpen" />
  </section>
</template>
